import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";

/**
 * Horizontal, snap-scrolling gallery of shipped projects.
 *
 * A grid stops working once the portfolio outgrows two or three entries: every
 * card shrinks, and screenshots are the whole point of a work section. A slider
 * keeps each card big enough to actually read while leaving room to add
 * projects without redesigning the section.
 *
 * Native scroll-snap rather than a carousel dependency — it keeps touch/trackpad
 * swipe, keyboard arrows and screen-reader order working for free, and adds no
 * package to a tree where a missing dependency means a prod 502.
 */
const WorkCarousel = ({ items = [], featuredLabel = "Featured build", visitLabel = "Visit the site" }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncPosition = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    setAtStart(scrollLeft <= 4);
    // A sub-pixel gap is normal at the end of a snap track, so allow slack.
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 4);

    const cards = Array.from(track.children);
    let nearest = 0;
    let smallest = Infinity;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - track.offsetLeft - scrollLeft);
      if (distance < smallest) {
        smallest = distance;
        nearest = i;
      }
    });
    setIndex(nearest);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    syncPosition();
    track.addEventListener("scroll", syncPosition, { passive: true });
    window.addEventListener("resize", syncPosition);
    return () => {
      track.removeEventListener("scroll", syncPosition);
      window.removeEventListener("resize", syncPosition);
    };
  }, [syncPosition]);

  const scrollToCard = (i) => {
    const track = trackRef.current;
    if (!track) return;

    const card = track.children[Math.max(0, Math.min(i, track.children.length - 1))];
    if (!card) return;

    // scrollBy/scrollTo with behavior:"smooth" ignores the CSS reduced-motion
    // rule in index.css, so honour the preference here instead.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({
      left: card.offsetLeft - track.offsetLeft,
      behavior: reduced ? "auto" : "smooth",
    });
  };

  if (!items.length) return null;

  const arrowClass =
    "flex h-10 w-10 items-center justify-center rounded-full border border-gray-700 text-silver-medium transition-colors duration-300 hover:border-moss hover:text-moss-light disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-gray-700 disabled:hover:text-silver-medium";

  return (
    <div className="mx-auto max-w-6xl">
      <div
        ref={trackRef}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Projects we have shipped"
        className="scrollbar-hide flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-moss"
      >
        {items.map((project) => (
          <figure
            key={project.name}
            /* Cards stop short of filling the row so the next one always peeks —
               the slider has to look scrollable without relying on the arrows. */
            className="flex w-[85%] shrink-0 snap-start flex-col overflow-hidden rounded-lg border border-gray-800 bg-off-black transition-colors duration-300 hover:border-moss/40 sm:w-[60%] lg:w-[calc(46%-0.75rem)]"
          >
            <img
              src={project.image}
              alt={`${project.name} — screenshot of the delivered project`}
              className="h-56 w-full object-cover object-top"
              loading="lazy"
            />
            <figcaption className="flex flex-1 flex-col p-6">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <h3 className="font-medium text-silver-light">{project.name}</h3>
                {project.tag && (
                  <span className="rounded-full border border-gray-700 px-2 py-0.5 text-[11px] uppercase tracking-wider text-silver-dark">
                    {project.tag}
                  </span>
                )}
                {project.featured && (
                  <span className="rounded-full border border-moss/50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-moss-light">
                    {featuredLabel}
                  </span>
                )}
              </div>
              <p className="flex-1 text-sm leading-relaxed text-silver-medium">{project.blurb}</p>
              {project.href && (
                <a
                  href={project.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 self-start text-sm font-medium text-moss-light hover:text-moss"
                >
                  {visitLabel}
                  <FaArrowRight />
                </a>
              )}
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {items.map((project, i) => (
            <button
              key={project.name}
              type="button"
              onClick={() => scrollToCard(i)}
              aria-label={`Show ${project.name}`}
              aria-current={i === index}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-moss" : "w-1.5 bg-gray-700 hover:bg-gray-600"
                }`}
            />
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => scrollToCard(index - 1)}
            disabled={atStart}
            aria-label="Previous project"
            className={arrowClass}
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <button
            type="button"
            onClick={() => scrollToCard(index + 1)}
            disabled={atEnd}
            aria-label="Next project"
            className={arrowClass}
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkCarousel;
